import { useEffect, useState } from "react";

import useAxios from "@hooks/use-axios";
import { OPS_ENDPOINTS } from "@src/utility/constants/endpoints";

const DEFAULT_LIMIT = 10;

const useDebitNotesList = () => {
    const { axiosInstance, loading } = useAxios({ base: "ops", initialLoader: true });
    const { axiosInstance: invoicesAxiosInstance } = useAxios({ base: "ops" });
    const [items, setItems] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: DEFAULT_LIMIT });
    // The note the revert modal is open for, if any.
    const [revertTarget, setRevertTarget] = useState(null);

    const fetchDebitNotes = async (page = 1, limit = DEFAULT_LIMIT) => {
        try {
            const res = await axiosInstance.get(OPS_ENDPOINTS.DEBIT_NOTES, { params: { page, limit } });
            const result = res.data || res;
            const list = result.data || result.items || result.results || [];
            setItems(list);
            setPagination({
                total: result.pagination?.total ?? result.total ?? list.length,
                page : result.pagination?.page ?? result.page ?? page,
                limit: result.pagination?.limit ?? result.limit ?? limit
            });
        } catch {
            setItems([]);
            setPagination({ total: 0, page, limit });
        }
    };

    useEffect(() => {
        fetchDebitNotes(1, DEFAULT_LIMIT);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePageChange = (limit, page) => fetchDebitNotes(page, limit);
    const handlePerRowsChange = (limit) => fetchDebitNotes(1, limit);

    // Notes here span brands, so every per-note endpoint needs the note's own account.
    const accountOf = (noteId) => items.find((item) => item._id === noteId)?.account_id;

    const updateStatus = async (noteId, status, reason) => {
        await axiosInstance.patch(OPS_ENDPOINTS.DEBIT_NOTE_STATUS(accountOf(noteId), noteId), reason ? { status, reason } : { status });
        await fetchDebitNotes(pagination.page, pagination.limit);
    };

    const revertNote = async (noteId, reason) => {
        await axiosInstance.patch(OPS_ENDPOINTS.DEBIT_NOTE_REVERT(accountOf(noteId), noteId), reason ? { reason } : undefined);
        await fetchDebitNotes(pagination.page, pagination.limit);
    };

    const markAsReceived = (noteId) => updateStatus(noteId, "fund_received");
    const issueToBrand = (noteId) => updateStatus(noteId, "issued");

    const fetchInvoices = async (noteId) => {
        try {
            const res = await invoicesAxiosInstance.get(OPS_ENDPOINTS.DEBIT_NOTE_INVOICES(accountOf(noteId), noteId), { skipSessionRedirect: true });
            const result = res.data || res;
            return result.items || result.data || result.results || [];
        } catch {
            return [];
        }
    };

    return {
        items,
        pagination,
        loading,
        revertTarget,
        openRevert : setRevertTarget,
        closeRevert: () => setRevertTarget(null),
        revertNote,
        markAsReceived,
        issueToBrand,
        fetchInvoices,
        handlePageChange,
        handlePerRowsChange
    };
};

export default useDebitNotesList;
